/**
 * API utility functions for making HTTP requests
 */

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Create axios instance with default config
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Simple request throttling - track requests in progress
const pendingRequests = {};

// Request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      // Token should already include 'Bearer ' prefix from authService
      config.headers.Authorization = token;
    }

    // Skip deduplication for file uploads
    if (config.headers['Content-Type'] === 'multipart/form-data') {
      return config;
    }

    // Create a unique key for this request
    const requestKey = `${config.method}:${config.url}:${JSON.stringify(config.params || {})}:${JSON.stringify(config.data || {})}`;
    
    // Add a property to track this request
    config._requestKey = requestKey;

    // If we have a pending identical request, cancel this one
    if (pendingRequests[requestKey]) {
      // console.log(`Preventing duplicate request: ${config.url}`);
      
      // Cancel the request - axios will handle this properly
      const source = axios.CancelToken.source();
      config.cancelToken = source.token;
      source.cancel('Duplicate request canceled');
    } else {
      // Mark this request as pending
      pendingRequests[requestKey] = true;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    // Clean up the pending request reference
    if (response.config._requestKey) {
      delete pendingRequests[response.config._requestKey];
    }
    
    // Return the data from the response
    return response.data;
  },
  (error) => {
    // Don't handle cancel errors specially
    if (axios.isCancel(error)) {
      // This is an expected cancellation (duplicate request)
      return Promise.reject(error);
    }
    
    // For actual errors, clean up pending request if applicable
    if (error.config?._requestKey) {
      delete pendingRequests[error.config._requestKey];
    }

    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem('authToken');
      localStorage.removeItem('userData');
      window.location.href = '/login';
    } else if (error.response) {
      console.error(`API Error (${error.response.status}): ${error.config?.url}`, error.response.data);
    } else if (error.request) {
      console.error(`API No Response: ${error.config?.url}`, error.request);
    } else {
      console.error(`API Request Error: ${error.message}`);
    }
    
    return Promise.reject(error);
  }
);

/**
 * Make a request with form data
 * @param {string} endpoint - API endpoint
 * @param {Object} formData - Form data to send
 * @param {Object} config - Additional axios config
 * @returns {Promise<any>} - Response data
 */
export const postForm = (endpoint, formData, config = {}) => {
  const formDataObj = new FormData();
  
  // Convert object to FormData
  Object.keys(formData).forEach(key => {
    formDataObj.append(key, formData[key]);
  });
  
  return axiosInstance.post(endpoint, formDataObj, {
    ...config,
    headers: {
      ...config.headers,
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const get = (endpoint, config = {}) => axiosInstance.get(endpoint, config);
export const post = (endpoint, data, config = {}) => axiosInstance.post(endpoint, data, config);
export const put = (endpoint, data, config = {}) => axiosInstance.put(endpoint, data, config);
export const del = (endpoint, config = {}) => axiosInstance.delete(endpoint, config);

// Create the API object
const apiUtils = {
  get,
  post,
  put,
  delete: del,
  postForm
};

export default apiUtils;
